import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import AdminLayout from '../../../components/Admin/Layout/AdminLayout';
import ModuleLayout from '../../../layouts/ModuleLayout';
import {
  Brain,
  Package,
  Users,
  TrendingUp,
  TrendingDown,
  Utensils,
  Check,
  X,
  Globe,
  ChevronDown,
  ChevronRight,
  Send,
  Lightbulb,
  AlertTriangle,
  Target,
  Clock,
  DollarSign,
  BarChart2,
  RefreshCw,
  Zap,
  TrendingUp as Forecast,
  MessageSquare,
  User,
  Loader2,
  Copy,
  ThumbsUp,
  ThumbsDown,
  Sparkles
} from 'lucide-react';

/**
 * AI Insights Page - Restaurant Management AI Engine
 * Comprehensive predictions, recommendations, and suggestions
 * With functional Ask AI chat interface
 */

// Language configurations
const languages = [
  { code: 'en', label: 'English' },
  { code: 'hi', label: 'हिंदी' },
  { code: 'ur', label: 'اردو' },
  { code: 'ta', label: 'தமிழ்' },
  { code: 'te', label: 'తెలుగు' },
];

// Translations
const translations = {
  en: {
    pageTitle: 'AI Insights Engine',
    pageSubtitle: 'Intelligent predictions, recommendations & suggestions powered by AI',
    askPlaceholder: 'Ask anything about your restaurant...',
    filters: { all: 'All Insights', predictions: 'Predictions', recommendations: 'Recommendations', alerts: 'Alerts', suggestions: 'Suggestions' },
    apply: 'Take Action',
    dismiss: 'Dismiss',
    confidence: 'Confidence',
    impact: 'Impact',
    timeframe: 'Timeframe',
    basedOn: 'Based on',
    refresh: 'Refresh Insights',
    categories: { inventory: 'Inventory', menu: 'Menu', staff: 'Staff', sales: 'Sales', customers: 'Customers', operations: 'Operations', finance: 'Finance' },
    aiThinking: 'AI is analyzing...',
    askAI: 'Ask AI',
    you: 'You',
    ai: 'AI Assistant',
    copied: 'Copied!'
  },
  hi: {
    pageTitle: 'AI इनसाइट्स इंजन',
    pageSubtitle: 'AI द्वारा संचालित बुद्धिमान भविष्यवाणियां, सिफारिशें और सुझाव',
    askPlaceholder: 'अपने रेस्तरां के बारे में कुछ भी पूछें...',
    filters: { all: 'सभी इनसाइट्स', predictions: 'भविष्यवाणियां', recommendations: 'सिफारिशें', alerts: 'अलर्ट', suggestions: 'सुझाव' },
    apply: 'कार्रवाई करें',
    dismiss: 'खारिज करें',
    confidence: 'विश्वास',
    impact: 'प्रभाव',
    timeframe: 'समय सीमा',
    basedOn: 'आधारित',
    refresh: 'रिफ्रेश करें',
    categories: { inventory: 'इन्वेंटरी', menu: 'मेनू', staff: 'स्टाफ', sales: 'बिक्री', customers: 'ग्राहक', operations: 'संचालन', finance: 'वित्त' },
    aiThinking: 'AI विश्लेषण कर रहा है...',
    askAI: 'AI से पूछें',
    you: 'आप',
    ai: 'AI सहायक',
    copied: 'कॉपी किया!'
  },
  ur: {
    pageTitle: 'AI انسائٹس انجن',
    pageSubtitle: 'AI سے چلنے والی ذہین پیشگوئیاں، سفارشات اور تجاویز',
    askPlaceholder: 'اپنے ریستوران کے بارے میں کچھ بھی پوچھیں...',
    filters: { all: 'تمام انسائٹس', predictions: 'پیشگوئیاں', recommendations: 'سفارشات', alerts: 'الرٹس', suggestions: 'تجاویز' },
    apply: 'کارروائی کریں',
    dismiss: 'خارج کریں',
    confidence: 'اعتماد',
    impact: 'اثر',
    timeframe: 'وقت کی حد',
    basedOn: 'بنیاد',
    refresh: 'تازہ کریں',
    categories: { inventory: 'انوینٹری', menu: 'مینو', staff: 'عملہ', sales: 'فروخت', customers: 'گاہک', operations: 'آپریشنز', finance: 'مالیات' },
    aiThinking: 'AI تجزیہ کر رہا ہے...',
    askAI: 'AI سے پوچھیں',
    you: 'آپ',
    ai: 'AI معاون',
    copied: 'کاپی ہو گیا!'
  },
  ta: {
    pageTitle: 'AI நுண்ணறிவு இயந்திரம்',
    pageSubtitle: 'AI ஆல் இயக்கப்படும் புத்திசாலித்தனமான கணிப்புகள், பரிந்துரைகள் மற்றும் ஆலோசனைகள்',
    askPlaceholder: 'உங்கள் உணவகத்தைப் பற்றி எதையும் கேளுங்கள்...',
    filters: { all: 'அனைத்து நுண்ணறிவுகள்', predictions: 'கணிப்புகள்', recommendations: 'பரிந்துரைகள்', alerts: 'எச்சரிக்கைகள்', suggestions: 'ஆலோசனைகள்' },
    apply: 'நடவடிக்கை எடு',
    dismiss: 'நிராகரி',
    confidence: 'நம்பிக்கை',
    impact: 'தாக்கம்',
    timeframe: 'காலக்கெடு',
    basedOn: 'அடிப்படையாக',
    refresh: 'புதுப்பி',
    categories: { inventory: 'சரக்கு', menu: 'மெனு', staff: 'பணியாளர்', sales: 'விற்பனை', customers: 'வாடிக்கையாளர்', operations: 'செயல்பாடுகள்', finance: 'நிதி' },
    aiThinking: 'AI பகுப்பாய்வு செய்கிறது...',
    askAI: 'AI யிடம் கேளுங்கள்',
    you: 'நீங்கள்',
    ai: 'AI உதவியாளர்',
    copied: 'நகலெடுக்கப்பட்டது!'
  },
  te: {
    pageTitle: 'AI ఇన్‌సైట్స్ ఇంజిన్',
    pageSubtitle: 'AI ద్వారా నడిచే తెలివైన అంచనాలు, సిఫార్సులు మరియు సూచనలు',
    askPlaceholder: 'మీ రెస్టారెంట్ గురించి ఏదైనా అడగండి...',
    filters: { all: 'అన్ని ఇన్‌సైట్స్', predictions: 'అంచనాలు', recommendations: 'సిఫార్సులు', alerts: 'అలర్ట్‌లు', suggestions: 'సూచనలు' },
    apply: 'చర్య తీసుకో',
    dismiss: 'తీసివేయి',
    confidence: 'నమ్మకం',
    impact: 'ప్రభావం',
    timeframe: 'సమయ వ్యవధి',
    basedOn: 'ఆధారం',
    refresh: 'రిఫ్రెష్',
    categories: { inventory: 'ఇన్వెంటరీ', menu: 'మెనూ', staff: 'సిబ్బంది', sales: 'అమ్మకాలు', customers: 'కస్టమర్లు', operations: 'ఆపరేషన్స్', finance: 'ఫైనాన్స్' },
    aiThinking: 'AI విశ్లేషిస్తోంది...',
    askAI: 'AI ని అడగండి',
    you: 'మీరు',
    ai: 'AI అసిస్టెంట్',
    copied: 'కాపీ చేయబడింది!'
  }
};

// AI Response Generator - Simulates intelligent responses based on query
const generateAIResponse = (query, language = 'en') => {
  const lowerQuery = query.toLowerCase();

  // Response templates based on keywords
  const responses = {
    // Sales related
    sales: {
      en: `📊 **Sales Analysis**

Based on your data from the last 30 days:

• **Total Revenue:** ₹14,52,340
• **Average Daily Sales:** ₹48,411
• **Peak Day:** Saturday (₹72,500 avg)
• **Best Performing Hours:** 7 PM - 10 PM

**Key Insights:**
1. Weekend sales are 45% higher than weekdays
2. Dinner contributes 62% of daily revenue
3. Online orders have grown 23% this month

**Recommendation:** Consider extending weekend hours and adding more staff during 7-10 PM peak.`,
      hi: `📊 **बिक्री विश्लेषण**

पिछले 30 दिनों के डेटा के आधार पर:

• **कुल राजस्व:** ₹14,52,340
• **औसत दैनिक बिक्री:** ₹48,411
• **पीक दिन:** शनिवार (₹72,500 औसत)

**मुख्य अंतर्दृष्टि:**
1. वीकेंड की बिक्री वीकडेज़ से 45% अधिक है
2. डिनर दैनिक राजस्व का 62% योगदान करता है`
    },

    // Revenue related
    revenue: {
      en: `💰 **Revenue Overview**

**This Month's Performance:**
• Total Revenue: ₹14,52,340
• vs Last Month: +8.5% (₹12,38,000)
• vs Same Month Last Year: +22%

**Revenue Breakdown:**
• Dine-in: ₹9,24,000 (64%)
• Takeaway: ₹3,12,000 (21%)
• Online Orders: ₹2,16,340 (15%)

**Top Revenue Items:**
1. Butter Chicken - ₹1,24,500
2. Biryani Family Pack - ₹98,400
3. Paneer Tikka - ₹76,200

**Growth Opportunity:** Online orders are growing fastest. Consider promotions to boost this channel.`,
      hi: `💰 **राजस्व अवलोकन**

**इस महीने का प्रदर्शन:**
• कुल राजस्व: ₹14,52,340
• पिछले महीने की तुलना में: +8.5%

**शीर्ष राजस्व आइटम:**
1. बटर चिकन - ₹1,24,500
2. बिरयानी फैमिली पैक - ₹98,400`
    },

    // Best selling / popular items
    best: {
      en: `🏆 **Best Selling Items Analysis**

**Top 10 Items by Quantity (Last 30 Days):**

| Rank | Item | Qty Sold | Revenue |
|------|------|----------|---------|
| 1 | Chicken Biryani | 847 | ₹2,54,100 |
| 2 | Butter Chicken | 623 | ₹1,86,900 |
| 3 | Paneer Butter Masala | 589 | ₹1,47,250 |
| 4 | Garlic Naan | 1,245 | ₹62,250 |
| 5 | Dal Makhani | 534 | ₹80,100 |

**Insights:**
• Chicken Biryani is your star performer
• Naan has highest volume but low revenue per unit
• Paneer items popular with vegetarian customers

**Suggestion:** Create a "Biryani Combo" with raita and salad to increase average ticket.`,
      hi: `🏆 **सबसे ज्यादा बिकने वाले आइटम**

**पिछले 30 दिनों में टॉप 5:**
1. चिकन बिरयानी - 847 यूनिट
2. बटर चिकन - 623 यूनिट
3. पनीर बटर मसाला - 589 यूनिट`
    },

    // Inventory / Stock
    inventory: {
      en: `📦 **Inventory Status Report**

**Critical Stock Alerts (Will run out in 3 days):**
🔴 Chicken: 25kg remaining (Need 40kg)
🔴 Paneer: 8kg remaining (Need 15kg)
🟡 Cooking Oil: 20L remaining (Need 30L)

**Well Stocked:**
🟢 Rice: 150kg (15 days supply)
🟢 Flour: 80kg (10 days supply)
🟢 Spices: Adequate

**Cost Analysis:**
• Total inventory value: ₹1,85,000
• Wastage this month: ₹12,400 (6.7%)
• Wastage reduced by 2.3% vs last month

**Action Required:**
Place order for Chicken and Paneer by tomorrow to avoid stockout before weekend rush.`,
      hi: `📦 **इन्वेंट्री स्थिति रिपोर्ट**

**महत्वपूर्ण स्टॉक अलर्ट:**
🔴 चिकन: 25kg बचा (40kg चाहिए)
🔴 पनीर: 8kg बचा (15kg चाहिए)

**कार्रवाई आवश्यक:**
वीकेंड से पहले चिकन और पनीर का ऑर्डर दें।`
    },

    // Stock
    stock: {
      en: `📦 **Stock Analysis**

**Current Stock Levels:**

| Item | Current | Required | Status |
|------|---------|----------|--------|
| Chicken | 25kg | 40kg | ⚠️ Low |
| Paneer | 8kg | 15kg | ⚠️ Low |
| Rice | 150kg | 50kg | ✅ OK |
| Oil | 20L | 30L | ⚠️ Medium |

**Predicted Requirements (Next 7 Days):**
Based on historical patterns and upcoming weekend:
• Chicken: 45kg
• Paneer: 18kg
• Vegetables: 60kg

**Recommendation:** Order chicken and paneer today for delivery by Thursday.`
    },

    // Customer related
    customer: {
      en: `👥 **Customer Analytics**

**Customer Overview:**
• Total Customers (This Month): 2,847
• New Customers: 423 (15%)
• Returning Customers: 2,424 (85%)
• Average Visit Frequency: 2.3 times/month

**Customer Segments:**
• Regular (5+ visits): 324 customers - ₹8,45,000 revenue
• Occasional (2-4 visits): 876 customers - ₹4,23,000 revenue
• One-time: 1,647 customers - ₹1,84,340 revenue

**At-Risk Customers:**
12 regular customers haven't visited in 3+ weeks. Combined lifetime value: ₹4,50,000

**Recommendation:** Send personalized "We Miss You" offers with 15% discount to at-risk customers.`,
      hi: `👥 **ग्राहक विश्लेषण**

**ग्राहक अवलोकन:**
• कुल ग्राहक (इस महीने): 2,847
• नए ग्राहक: 423 (15%)
• लौटने वाले ग्राहक: 2,424 (85%)

**जोखिम में ग्राहक:**
12 नियमित ग्राहक 3+ हफ्ते से नहीं आए।`
    },

    // Staff / Employee
    staff: {
      en: `👨‍🍳 **Staff Performance Analysis**

**Current Staffing:**
• Total Staff: 12
• Kitchen: 5
• Service: 4
• Delivery: 3

**Performance Metrics:**
| Staff | Orders/Day | Avg Rating | Efficiency |
|-------|-----------|------------|------------|
| Rahul (Kitchen) | 45 | 4.8 | 94% |
| Priya (Service) | 38 | 4.9 | 92% |
| Amit (Kitchen) | 42 | 4.6 | 89% |

**Peak Hour Analysis:**
Friday 7-10 PM needs 2 additional staff. Current wait time: 12 mins vs target 8 mins.

**Recommendation:**
1. Schedule Rahul and Priya for Friday evening shift
2. Consider hiring 1 part-time staff for weekends`,
      hi: `👨‍🍳 **स्टाफ प्रदर्शन विश्लेषण**

**वर्तमान स्टाफिंग:**
• कुल स्टाफ: 12
• किचन: 5
• सर्विस: 4

**सिफारिश:**
शुक्रवार शाम के लिए 2 अतिरिक्त स्टाफ शेड्यूल करें।`
    },

    // Menu related
    menu: {
      en: `🍽️ **Menu Performance Analysis**

**Category Performance:**
| Category | Items | Revenue | Margin |
|----------|-------|---------|--------|
| Main Course | 24 | ₹8,45,000 | 42% |
| Starters | 18 | ₹2,34,000 | 55% |
| Beverages | 12 | ₹1,56,000 | 68% |
| Desserts | 8 | ₹98,000 | 52% |

**Hidden Gems (High Margin, Low Sales):**
• Paneer Tikka: 52% margin, only 8 orders/day
• Mushroom Biryani: 48% margin, only 5 orders/day
• Gulab Jamun: 65% margin, only 12 orders/day

**Underperformers (Consider Removing):**
• Veg Manchurian: 2 orders/day, 28% margin
• Plain Rice: Low margin, commonly substituted

**Recommendation:** Feature Paneer Tikka as "Chef's Special" - can boost daily revenue by ₹3,200.`,
      hi: `🍽️ **मेनू प्रदर्शन विश्लेषण**

**छिपे हुए रत्न (उच्च मार्जिन, कम बिक्री):**
• पनीर टिक्का: 52% मार्जिन, केवल 8 ऑर्डर/दिन

**सिफारिश:**
पनीर टिक्का को "शेफ स्पेशल" के रूप में फीचर करें।`
    },

    // Profit related
    profit: {
      en: `💵 **Profit Analysis**

**Monthly P&L Summary:**
• Gross Revenue: ₹14,52,340
• Cost of Goods Sold: ₹5,23,000 (36%)
• Gross Profit: ₹9,29,340 (64%)

**Operating Expenses:**
• Staff Salaries: ₹2,40,000
• Rent: ₹85,000
• Utilities: ₹32,000
• Marketing: ₹25,000
• Other: ₹48,000

**Net Profit: ₹4,99,340 (34.4%)**

**Comparison:**
• vs Last Month: +₹42,000 (+9.2%)
• vs Industry Avg: +4.4% above

**Cost Optimization Opportunities:**
1. Switch vegetable supplier - Save ₹18,000/month
2. Reduce food wastage - Save ₹8,000/month
3. Energy efficient equipment - Save ₹5,000/month`,
      hi: `💵 **लाभ विश्लेषण**

**मासिक सारांश:**
• कुल राजस्व: ₹14,52,340
• शुद्ध लाभ: ₹4,99,340 (34.4%)`
    },

    // Prediction / Forecast
    predict: {
      en: `🔮 **Sales Predictions**

**Next 7 Days Forecast:**
| Day | Predicted Sales | Confidence |
|-----|-----------------|------------|
| Monday | ₹38,000 | 85% |
| Tuesday | ₹42,000 | 87% |
| Wednesday | ₹45,000 | 84% |
| Thursday | ₹48,000 | 86% |
| Friday | ₹68,000 | 92% |
| Saturday | ₹75,000 | 94% |
| Sunday | ₹58,000 | 88% |

**Weekly Total: ₹3,74,000** (vs ₹3,52,000 last week)

**Factors Considered:**
• Historical patterns (45 days)
• Weather forecast (Pleasant weekend)
• Local events (Cricket match Saturday)
• Seasonal trends

**Preparation Needed:**
• Order 20kg extra chicken by Tuesday
• Schedule 2 additional staff for Friday-Saturday`,
      hi: `🔮 **बिक्री भविष्यवाणी**

**अगले 7 दिनों का पूर्वानुमान:**
• शुक्रवार: ₹68,000 (92% विश्वास)
• शनिवार: ₹75,000 (94% विश्वास)`
    },

    // Weekend
    weekend: {
      en: `📅 **Weekend Forecast & Preparation**

**Expected Performance:**
• Friday: ₹68,000 (+42% vs weekday avg)
• Saturday: ₹75,000 (+57% vs weekday avg)
• Sunday: ₹58,000 (+21% vs weekday avg)

**Peak Hours:** 7 PM - 10 PM (65% of daily orders)

**Required Preparations:**

**Inventory:**
• Chicken: Order 20kg extra
• Paneer: Order 8kg extra
• Beverages: Stock 30% more

**Staffing:**
• Friday: Add 2 staff (6 PM - 11 PM)
• Saturday: Add 3 staff (5 PM - 11 PM)
• Kitchen backup: Keep 1 on standby

**Special Considerations:**
• Cricket match on Saturday - expect 20% more orders during 8-10 PM
• Pre-prepare marinades Thursday night
• Stock extra packaging for takeaway surge`
    },

    // Today
    today: {
      en: `📊 **Today's Performance (Real-time)**

**Sales So Far:**
• Revenue: ₹32,450 (as of now)
• Orders: 67
• Average Ticket: ₹484

**Comparison:**
• vs Yesterday (same time): +12%
• vs Last Week (same day): +8%

**Today's Top Items:**
1. Chicken Biryani - 23 orders
2. Butter Chicken - 18 orders
3. Paneer Tikka - 12 orders

**Current Status:**
• Kitchen Load: 78% capacity
• Average Prep Time: 18 mins
• Pending Orders: 8

**Forecast for Rest of Day:**
Expected to reach ₹52,000 by closing (confidence: 88%)

**Alert:** Order volume picking up - consider calling backup staff if needed.`
    },

    // Suggest / Recommendation
    suggest: {
      en: `💡 **AI Recommendations for Your Restaurant**

Based on comprehensive analysis, here are top actions to improve performance:

**Immediate Actions (This Week):**
1. 🍗 **Stock Alert:** Order chicken and paneer today to avoid weekend stockout
2. 👥 **Staffing:** Schedule 2 extra staff for Friday 7-10 PM
3. 📧 **Customer Retention:** Send offers to 12 at-risk regular customers

**Short-term (This Month):**
4. 🍽️ **Menu Optimization:** Feature Paneer Tikka as special - potential ₹3,200/day extra
5. 🍱 **Lunch Combo:** Launch ₹199 combo to boost afternoon sales by 40%
6. 📦 **Supplier:** Switch to Fresh Farms - Save ₹18,000/month

**Growth Opportunities:**
7. 📱 **Online Orders:** Growing 23% - invest in digital marketing
8. 🎉 **Weekend Events:** Host theme nights to boost Saturday revenue
9. 🏆 **Loyalty Program:** Implement to increase repeat visits by 25%

**Estimated Impact:** Following all recommendations could increase monthly profit by ₹85,000-1,20,000.`
    },

    // Help / What can you do
    help: {
      en: `🤖 **AI Assistant - How Can I Help?**

I can analyze your restaurant data and provide insights on:

**📊 Sales & Revenue**
• "What are my sales today?"
• "Show revenue trends"
• "Compare this month vs last month"

**🍽️ Menu Analysis**
• "What are my best selling items?"
• "Which items have highest profit margin?"
• "Menu optimization suggestions"

**📦 Inventory**
• "What's my current stock status?"
• "When will chicken run out?"
• "Inventory recommendations"

**👥 Customers**
• "Customer analytics"
• "Who are my top customers?"
• "At-risk customer analysis"

**👨‍🍳 Staff**
• "Staff performance report"
• "Peak hour staffing needs"

**🔮 Predictions**
• "Weekend forecast"
• "Next week sales prediction"
• "What should I prepare for Friday?"

**💡 Recommendations**
• "Give me suggestions to improve"
• "How can I increase profit?"

Just type your question and I'll analyze your data!`,
      hi: `🤖 **AI सहायक - मैं कैसे मदद कर सकता हूं?**

मैं आपके रेस्तरां डेटा का विश्लेषण कर सकता हूं:

• बिक्री और राजस्व विश्लेषण
• मेनू प्रदर्शन
• इन्वेंटरी स्थिति
• ग्राहक विश्लेषण
• स्टाफ प्रदर्शन
• भविष्यवाणियां और सिफारिशें

बस अपना सवाल टाइप करें!`
    },

    // Default response
    default: {
      en: `I've analyzed your query. Here's what I found:

Based on your restaurant's current data:

**Quick Overview:**
• Today's Revenue: ₹32,450 (67 orders)
• This Week: ₹2,48,000 (+8% vs last week)
• Top Item: Chicken Biryani (23 orders today)

**Key Insights:**
1. Sales are trending 8% above last week
2. Weekend is expected to be busy (cricket match)
3. Chicken stock needs replenishment by Thursday

**Would you like more details on:**
• Sales analysis
• Menu performance
• Inventory status
• Customer insights
• Staff performance
• Predictions & recommendations

Just ask a more specific question and I'll provide detailed analysis!`,
      hi: `मैंने आपके प्रश्न का विश्लेषण किया है।

**त्वरित अवलोकन:**
• आज का राजस्व: ₹32,450
• इस सप्ताह: ₹2,48,000

अधिक विस्तृत विश्लेषण के लिए विशिष्ट प्रश्न पूछें!`
    }
  };

  // Match keywords to responses
  const keywordMatches = [
    { keywords: ['sale', 'sales', 'selling', 'sold'], response: 'sales' },
    { keywords: ['revenue', 'earning', 'income'], response: 'revenue' },
    { keywords: ['best', 'top', 'popular', 'highest'], response: 'best' },
    { keywords: ['inventory', 'ingredient'], response: 'inventory' },
    { keywords: ['stock', 'supply', 'supplies'], response: 'stock' },
    { keywords: ['customer', 'client', 'guest', 'visitor'], response: 'customer' },
    { keywords: ['staff', 'employee', 'worker', 'team'], response: 'staff' },
    { keywords: ['menu', 'item', 'dish', 'food'], response: 'menu' },
    { keywords: ['profit', 'margin', 'cost', 'expense'], response: 'profit' },
    { keywords: ['predict', 'forecast', 'future', 'expect'], response: 'predict' },
    { keywords: ['weekend', 'saturday', 'sunday', 'friday'], response: 'weekend' },
    { keywords: ['today', 'now', 'current', 'live'], response: 'today' },
    { keywords: ['suggest', 'recommend', 'advice', 'improve', 'tip'], response: 'suggest' },
    { keywords: ['help', 'can you', 'what can', 'how to'], response: 'help' },
  ];

  // Find matching response
  for (const match of keywordMatches) {
    if (match.keywords.some(keyword => lowerQuery.includes(keyword))) {
      return responses[match.response]?.[language] || responses[match.response]?.en || responses.default.en;
    }
  }

  return responses.default[language] || responses.default.en;
};

// Comprehensive AI Insights Data
const insightsData = [
  {
    id: 1,
    type: 'prediction',
    category: 'sales',
    icon: TrendingUp,
    priority: 'high',
    confidence: 92,
    impact: 'high',
    timeframe: 'This Weekend',
    dataPoints: '45 days sales data',
    messages: {
      en: {
        title: 'Weekend Sales Surge Expected',
        summary: 'Based on historical patterns, weather forecast, and local events, we predict a 35% increase in footfall this Saturday.',
        details: 'Your restaurant typically sees higher traffic during pleasant weather weekends. Combined with the cricket match scheduled nearby, expect peak hours between 7-10 PM.',
        action: 'Prepare additional inventory and consider calling 2 extra staff members.'
      },
      hi: {
        title: 'वीकेंड सेल्स में बढ़ोतरी की उम्मीद',
        summary: 'ऐतिहासिक पैटर्न और मौसम के आधार पर, इस शनिवार को 35% अधिक फुटफॉल की भविष्यवाणी है।',
        details: 'आपके रेस्तरां में अच्छे मौसम वाले वीकेंड पर अधिक ट्रैफिक आता है।',
        action: 'अतिरिक्त इन्वेंट्री तैयार करें और 2 एक्स्ट्रा स्टाफ बुलाएं।'
      },
      ur: { title: 'ویک اینڈ سیلز میں اضافے کی توقع', summary: 'اس ہفتے 35% زیادہ فٹ فال کی پیشگوئی۔', details: '', action: '' },
      ta: { title: 'வார இறுதி விற்பனை அதிகரிப்பு', summary: '35% அதிக வருகை எதிர்பார்க்கப்படுகிறது.', details: '', action: '' },
      te: { title: 'వారాంతపు సేల్స్ పెరుగుదల', summary: '35% ఎక్కువ ఫుట్‌ఫాల్ అంచనా.', details: '', action: '' }
    }
  },
  {
    id: 2,
    type: 'prediction',
    category: 'inventory',
    icon: Package,
    priority: 'high',
    confidence: 88,
    impact: 'high',
    timeframe: 'Next 3 Days',
    dataPoints: '90 days consumption data',
    messages: {
      en: {
        title: 'Chicken Stock Will Run Out by Thursday',
        summary: 'Current consumption rate indicates chicken inventory will be depleted 2 days before your scheduled restock.',
        details: 'Average daily consumption is 12kg, current stock is 25kg. With the predicted weekend rush, you need at least 40kg.',
        action: 'Place emergency order for 20kg chicken by Tuesday evening.'
      },
      hi: { title: 'गुरुवार तक चिकन स्टॉक खत्म हो जाएगा', summary: 'वर्तमान खपत दर से चिकन इन्वेंट्री जल्द खत्म होगी।', details: '', action: '' },
      ur: { title: 'جمعرات تک چکن ختم', summary: 'موجودہ استعمال کی شرح سے۔', details: '', action: '' },
      ta: { title: 'வியாழன் வாக்கில் கோழி தீரும்', summary: 'தற்போதைய நுகர்வு விகிதத்தில்.', details: '', action: '' },
      te: { title: 'గురువారం నాటికి చికెన్ అయిపోతుంది', summary: 'ప్రస్తుత వినియోగ రేటులో.', details: '', action: '' }
    }
  },
  {
    id: 3,
    type: 'recommendation',
    category: 'menu',
    icon: Utensils,
    priority: 'medium',
    confidence: 85,
    impact: 'medium',
    timeframe: 'Immediate',
    dataPoints: '60 days order analysis',
    messages: {
      en: {
        title: 'Promote Paneer Tikka as Today\'s Special',
        summary: 'Paneer Tikka has the highest profit margin (52%) but ranks 15th in orders. Strategic promotion can boost daily revenue by ₹3,200.',
        details: 'This dish costs ₹85 to prepare and sells at ₹249. Customers who order it have 78% satisfaction rate.',
        action: 'Add to Today\'s Special board and train staff to recommend it.'
      },
      hi: { title: 'पनीर टिक्का को स्पेशल बनाएं', summary: '52% मार्जिन है लेकिन ऑर्डर कम हैं।', details: '', action: '' },
      ur: { title: 'پنیر ٹکا سپیشل', summary: '52% مارجن ہے۔', details: '', action: '' },
      ta: { title: 'பன்னீர் டிக்கா ஸ்பெஷல்', summary: '52% மார்ஜின்.', details: '', action: '' },
      te: { title: 'పనీర్ టిక్కా స్పెషల్', summary: '52% మార్జిన్.', details: '', action: '' }
    }
  },
  {
    id: 4,
    type: 'recommendation',
    category: 'staff',
    icon: Users,
    priority: 'high',
    confidence: 91,
    impact: 'high',
    timeframe: 'Friday',
    dataPoints: '30 days staffing analysis',
    messages: {
      en: {
        title: 'Schedule 2 Additional Staff for Friday Evening',
        summary: 'Friday 7-10 PM consistently shows 85% higher order volume. Current staffing causes 12-minute average wait time vs target of 8 minutes.',
        details: 'With current 4 staff members, kitchen efficiency drops to 67% during peak.',
        action: 'Contact Rahul and Priya for Friday evening shift (6 PM - 11 PM).'
      },
      hi: { title: 'शुक्रवार के लिए 2 स्टाफ जोड़ें', summary: '85% अधिक ऑर्डर वॉल्यूम।', details: '', action: '' },
      ur: { title: 'جمعہ کے لیے 2 سٹاف', summary: '85% زیادہ آرڈر۔', details: '', action: '' },
      ta: { title: 'வெள்ளிக்கு 2 பணியாளர்', summary: '85% அதிக ஆர்டர்.', details: '', action: '' },
      te: { title: 'శుక్రవారం 2 స్టాఫ్', summary: '85% ఎక్కువ ఆర్డర్.', details: '', action: '' }
    }
  },
  {
    id: 5,
    type: 'alert',
    category: 'customers',
    icon: AlertTriangle,
    priority: 'high',
    confidence: 94,
    impact: 'high',
    timeframe: 'Urgent',
    dataPoints: 'Customer behavior analysis',
    messages: {
      en: {
        title: '12 Regular Customers at Risk of Churning',
        summary: 'These high-value customers (avg spend ₹1,850/visit) haven\'t visited in 3+ weeks. Combined lifetime value at risk: ₹4,50,000.',
        details: 'Analysis shows these customers visited 2-3 times/month previously.',
        action: 'Send personalized "We Miss You" offers with 15% discount within 24 hours.'
      },
      hi: { title: '12 ग्राहक खोने का खतरा', summary: '₹4,50,000 लाइफटाइम वैल्यू।', details: '', action: '' },
      ur: { title: '12 گاہک خطرے میں', summary: '₹4,50,000 ویلیو۔', details: '', action: '' },
      ta: { title: '12 வாடிக்கையாளர் அபாயம்', summary: '₹4,50,000 மதிப்பு.', details: '', action: '' },
      te: { title: '12 కస్టమర్లు ప్రమాదంలో', summary: '₹4,50,000 విలువ.', details: '', action: '' }
    }
  },
  {
    id: 6,
    type: 'suggestion',
    category: 'operations',
    icon: Lightbulb,
    priority: 'medium',
    confidence: 79,
    impact: 'medium',
    timeframe: 'This Week',
    dataPoints: 'Operational efficiency analysis',
    messages: {
      en: {
        title: 'Introduce Lunch Combo to Boost Afternoon Sales',
        summary: 'Lunch hours (12-3 PM) contribute only 22% of daily revenue vs industry average of 35%. A ₹199 combo meal could add ₹56,000/month.',
        details: 'Analysis of 847 lunch orders shows average ticket is ₹285.',
        action: 'Create lunch combo menu and promote via SMS to nearby office areas.'
      },
      hi: { title: 'लंच कॉम्बो शुरू करें', summary: '₹56,000/माह जोड़ सकते हैं।', details: '', action: '' },
      ur: { title: 'لنچ کومبو شروع کریں', summary: '₹56,000/ماہ بڑھ سکتا ہے۔', details: '', action: '' },
      ta: { title: 'லஞ்ச் காம்போ', summary: '₹56,000/மாதம் சேர்க்கலாம்.', details: '', action: '' },
      te: { title: 'లంచ్ కాంబో', summary: '₹56,000/నెల పెరగవచ్చు.', details: '', action: '' }
    }
  }
];

// Type configuration
const typeConfig = {
  prediction: { label: 'Prediction', color: 'blue', icon: Forecast },
  recommendation: { label: 'Recommendation', color: 'green', icon: Target },
  alert: { label: 'Alert', color: 'red', icon: AlertTriangle },
  suggestion: { label: 'Suggestion', color: 'purple', icon: Lightbulb }
};

// Chat Message Component
function ChatMessage({ message, t, isLast }) {
  const [copied, setCopied] = useState(false);
  const isUser = message.role === 'user';

  const handleCopy = () => {
    navigator.clipboard.writeText(message.content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Parse markdown-like formatting
  const formatContent = (text) => {
    const lines = text.split('\n');
    return lines.map((line, i) => {
      // Bold text
      line = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
      // Headers
      if (line.startsWith('###')) {
        return <h4 key={i} className="font-semibold text-gray-900 mt-3 mb-1">{line.replace('###', '').trim()}</h4>;
      }
      if (line.startsWith('##')) {
        return <h3 key={i} className="font-semibold text-gray-900 mt-4 mb-2 text-lg">{line.replace('##', '').trim()}</h3>;
      }
      // Bullet points
      if (line.startsWith('•') || line.startsWith('-')) {
        return <li key={i} className="ml-4 mb-1" dangerouslySetInnerHTML={{ __html: line.substring(1).trim() }} />;
      }
      // Numbered lists
      if (/^\d+\./.test(line)) {
        return <li key={i} className="ml-4 mb-1 list-decimal" dangerouslySetInnerHTML={{ __html: line.replace(/^\d+\.\s*/, '') }} />;
      }
      // Table rows (simple)
      if (line.startsWith('|')) {
        return null; // Skip table formatting for simplicity
      }
      // Empty lines
      if (line.trim() === '') {
        return <br key={i} />;
      }
      // Regular text
      return <p key={i} className="mb-1" dangerouslySetInnerHTML={{ __html: line }} />;
    });
  };

  return (
    <div className={`flex gap-3 ${isUser ? 'flex-row-reverse' : ''}`}>
      {/* Avatar */}
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
        isUser ? 'bg-gray-200' : 'bg-gradient-to-br from-gray-800 to-gray-900'
      }`}>
        {isUser ? (
          <User size={16} className="text-gray-600" />
        ) : (
          <Brain size={16} className="text-white" />
        )}
      </div>

      {/* Message Content */}
      <div className={`flex-1 max-w-[85%] ${isUser ? 'text-right' : ''}`}>
        <div className={`text-xs text-gray-500 mb-1 ${isUser ? 'text-right' : ''}`}>
          {isUser ? t.you : t.ai}
        </div>
        <div className={`inline-block rounded-2xl px-4 py-3 ${
          isUser
            ? 'bg-gray-900 text-white rounded-tr-sm'
            : 'bg-white border border-gray-200 text-gray-700 rounded-tl-sm'
        }`}>
          {isUser ? (
            <p>{message.content}</p>
          ) : (
            <div className="text-sm leading-relaxed prose prose-sm max-w-none">
              {formatContent(message.content)}
            </div>
          )}
        </div>

        {/* Actions for AI messages */}
        {!isUser && (
          <div className="flex items-center gap-2 mt-2">
            <button
              onClick={handleCopy}
              className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1"
            >
              {copied ? <Check size={12} /> : <Copy size={12} />}
              {copied ? t.copied : 'Copy'}
            </button>
            <button className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1">
              <ThumbsUp size={12} />
            </button>
            <button className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1">
              <ThumbsDown size={12} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// Insight Card Component
function InsightCard({ insight, language, t, onApply, onDismiss }) {
  const [expanded, setExpanded] = useState(false);
  const msg = insight.messages[language] || insight.messages.en;
  const typeInfo = typeConfig[insight.type];

  const colorClasses = {
    blue: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', badge: 'bg-blue-100 text-blue-700' },
    green: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700', badge: 'bg-green-100 text-green-700' },
    red: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', badge: 'bg-red-100 text-red-700' },
    purple: { bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-700', badge: 'bg-purple-100 text-purple-700' }
  };

  const colors = colorClasses[typeInfo.color];
  const Icon = insight.icon;

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow">
      <div className="h-1" style={{ backgroundColor: typeInfo.color === 'blue' ? '#3b82f6' : typeInfo.color === 'green' ? '#22c55e' : typeInfo.color === 'red' ? '#ef4444' : '#a855f7' }}></div>

      <div className="p-5">
        <div className="flex items-start gap-4 mb-4">
          <div className={`w-12 h-12 ${colors.bg} ${colors.border} border rounded-lg flex items-center justify-center flex-shrink-0`}>
            <Icon size={24} className={colors.text} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <span className={`text-xs font-semibold px-2 py-0.5 rounded ${colors.badge}`}>
                {typeInfo.label}
              </span>
              <span className="text-xs text-gray-500 uppercase tracking-wide">
                {t.categories[insight.category]}
              </span>
            </div>
            <h3 className="font-semibold text-gray-900">{msg.title}</h3>
          </div>
        </div>

        <p className="text-gray-700 text-sm leading-relaxed mb-4">{msg.summary}</p>

        <div className="flex flex-wrap gap-4 text-xs text-gray-500 mb-4">
          <div className="flex items-center gap-1">
            <BarChart2 size={14} />
            <span>{t.confidence}: {insight.confidence}%</span>
          </div>
          <div className="flex items-center gap-1">
            <Zap size={14} />
            <span>{t.impact}: {insight.impact === 'high' ? 'High' : 'Medium'}</span>
          </div>
          <div className="flex items-center gap-1">
            <Clock size={14} />
            <span>{insight.timeframe}</span>
          </div>
        </div>

        {expanded && msg.details && (
          <div className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-100">
            <p className="text-sm text-gray-600 mb-3">{msg.details}</p>
            <div className="text-xs text-gray-500 flex items-center gap-1">
              <MessageSquare size={12} />
              <span>{t.basedOn}: {insight.dataPoints}</span>
            </div>
          </div>
        )}

        {expanded && msg.action && (
          <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-start gap-2">
              <Target size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <span className="text-xs font-semibold text-amber-700 uppercase">Recommended Action</span>
                <p className="text-sm text-amber-800 mt-1">{msg.action}</p>
              </div>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between pt-3 border-t border-gray-100">
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1"
          >
            {expanded ? 'Show Less' : 'Show Details'}
            <ChevronDown size={16} className={`transform transition-transform ${expanded ? 'rotate-180' : ''}`} />
          </button>

          <div className="flex items-center gap-2">
            <button
              onClick={() => onDismiss(insight.id)}
              className="px-3 py-1.5 text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              {t.dismiss}
            </button>
            <button
              onClick={() => onApply(insight.id)}
              className="px-4 py-1.5 text-sm font-medium text-white bg-gray-900 hover:bg-gray-800 rounded-lg transition-colors flex items-center gap-1"
            >
              {t.apply}
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AIInsightsPage() {
  const location = useLocation();
  const [language, setLanguage] = useState('en');
  const [showLangMenu, setShowLangMenu] = useState(false);
  const [insights, setInsights] = useState(insightsData);
  const [query, setQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Detect if accessed from AI Insights module directly
  const isAIModule = location.pathname.startsWith('/ai-insights');
  const Layout = isAIModule ? ModuleLayout : AdminLayout;

  // Chat state
  const [chatMessages, setChatMessages] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const chatEndRef = useRef(null);

  const t = translations[language];
  const isRTL = language === 'ur';

  // Scroll to bottom of chat
  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatMessages, isTyping]);

  const handleApply = (id) => {
    alert('Action initiated! (Demo mode)');
    setInsights(prev => prev.filter(i => i.id !== id));
  };

  const handleDismiss = (id) => {
    setInsights(prev => prev.filter(i => i.id !== id));
  };

  const handleAsk = async () => {
    if (!query.trim()) return;

    // Add user message
    const userMessage = { role: 'user', content: query };
    setChatMessages(prev => [...prev, userMessage]);
    setQuery('');
    setShowChat(true);
    setIsTyping(true);

    // Simulate AI thinking delay
    await new Promise(resolve => setTimeout(resolve, 1500 + Math.random() * 1000));

    // Generate AI response
    const aiResponse = generateAIResponse(query, language);

    // Add AI message
    setChatMessages(prev => [...prev, { role: 'assistant', content: aiResponse }]);
    setIsTyping(false);
  };

  const handleRefresh = () => {
    setIsRefreshing(true);
    setTimeout(() => {
      setIsRefreshing(false);
      setInsights(insightsData);
    }, 1500);
  };

  const filteredInsights = activeFilter === 'all'
    ? insights
    : insights.filter(i => i.type === activeFilter);

  const filterCounts = {
    all: insights.length,
    predictions: insights.filter(i => i.type === 'prediction').length,
    recommendations: insights.filter(i => i.type === 'recommendation').length,
    alerts: insights.filter(i => i.type === 'alert').length,
    suggestions: insights.filter(i => i.type === 'suggestion').length
  };

  // Quick prompts
  const quickPrompts = [
    "What are today's sales?",
    "Show best selling items",
    "Stock status report",
    "Weekend forecast",
    "Give me suggestions"
  ];

  return (
    <Layout>
      <div className={`min-h-full bg-gray-50 ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
        {/* Full Width Header */}
        <div className="bg-white border-b border-gray-200">
          <div className="px-6 py-5">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl flex items-center justify-center shadow-lg">
                  <Brain size={26} className="text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">{t.pageTitle}</h1>
                  <p className="text-gray-500 mt-0.5">{t.pageSubtitle}</p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <button
                  onClick={handleRefresh}
                  disabled={isRefreshing}
                  className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <RefreshCw size={18} className={isRefreshing ? 'animate-spin' : ''} />
                  <span className="text-sm">{t.refresh}</span>
                </button>

                <div className="relative">
                  <button
                    onClick={() => setShowLangMenu(!showLangMenu)}
                    className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
                  >
                    <Globe size={16} className="text-gray-500" />
                    <span className="text-sm font-medium text-gray-700">
                      {languages.find(l => l.code === language)?.label}
                    </span>
                    <ChevronDown size={14} className="text-gray-400" />
                  </button>

                  {showLangMenu && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setShowLangMenu(false)} />
                      <div className="absolute right-0 mt-1 w-40 bg-white border border-gray-200 rounded-lg shadow-lg z-20 py-1">
                        {languages.map(lang => (
                          <button
                            key={lang.code}
                            onClick={() => { setLanguage(lang.code); setShowLangMenu(false); }}
                            className={`w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-gray-50 ${
                              language === lang.code ? 'text-gray-900 font-medium' : 'text-gray-600'
                            }`}
                          >
                            {lang.label}
                            {language === lang.code && <Check size={14} />}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Ask AI Section */}
            <div className="mt-5">
              {/* Chat Messages */}
              {showChat && chatMessages.length > 0 && (
                <div className="mb-4 bg-gray-50 rounded-xl border border-gray-200 p-4 max-h-96 overflow-y-auto">
                  <div className="space-y-4">
                    {chatMessages.map((msg, idx) => (
                      <ChatMessage
                        key={idx}
                        message={msg}
                        t={t}
                        isLast={idx === chatMessages.length - 1}
                      />
                    ))}
                    {isTyping && (
                      <div className="flex gap-3">
                        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center">
                          <Brain size={16} className="text-white" />
                        </div>
                        <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-2xl rounded-tl-sm px-4 py-3">
                          <Loader2 size={16} className="animate-spin text-gray-400" />
                          <span className="text-sm text-gray-500">{t.aiThinking}</span>
                        </div>
                      </div>
                    )}
                    <div ref={chatEndRef} />
                  </div>
                </div>
              )}

              {/* Quick Prompts */}
              {!showChat && (
                <div className="flex flex-wrap gap-2 mb-3">
                  {quickPrompts.map((prompt, idx) => (
                    <button
                      key={idx}
                      onClick={() => { setQuery(prompt); }}
                      className="px-3 py-1.5 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-full transition-colors flex items-center gap-1"
                    >
                      <Sparkles size={12} />
                      {prompt}
                    </button>
                  ))}
                </div>
              )}

              {/* Input */}
              <div className="flex items-center gap-3">
                <div className="flex-1 relative">
                  <Brain size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleAsk()}
                    placeholder={t.askPlaceholder}
                    className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-gray-300 focus:border-gray-300"
                  />
                </div>
                <button
                  onClick={handleAsk}
                  disabled={!query.trim() || isTyping}
                  className="px-5 py-3 bg-gray-900 hover:bg-gray-800 disabled:bg-gray-300 text-white text-sm font-medium rounded-xl transition-colors flex items-center gap-2"
                >
                  {isTyping ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                  {t.askAI}
                </button>
              </div>

              {showChat && (
                <button
                  onClick={() => { setShowChat(false); setChatMessages([]); }}
                  className="mt-2 text-xs text-gray-500 hover:text-gray-700"
                >
                  Clear conversation
                </button>
              )}
            </div>
          </div>

          {/* Filter Tabs */}
          <div className="px-6 border-t border-gray-100">
            <div className="flex items-center gap-1 -mb-px overflow-x-auto">
              {Object.entries(t.filters).map(([key, label]) => (
                <button
                  key={key}
                  onClick={() => setActiveFilter(key)}
                  className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                    activeFilter === key
                      ? 'border-gray-900 text-gray-900'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {label}
                  <span className={`ml-1.5 px-1.5 py-0.5 text-xs rounded ${
                    activeFilter === key ? 'bg-gray-900 text-white' : 'bg-gray-200 text-gray-600'
                  }`}>
                    {filterCounts[key]}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Insights Grid */}
        <div className="px-6 py-6">
          {filteredInsights.length > 0 ? (
            <div className="grid lg:grid-cols-2 gap-5">
              {filteredInsights.map(insight => (
                <InsightCard
                  key={insight.id}
                  insight={insight}
                  language={language}
                  t={t}
                  onApply={handleApply}
                  onDismiss={handleDismiss}
                />
              ))}
            </div>
          ) : (
            <div className="bg-white border border-gray-200 rounded-lg py-16 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check size={32} className="text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-1">All caught up!</h3>
              <p className="text-gray-500">No pending insights at this time.</p>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
